import { __awaiter } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { checkoutMutations } from '@as-react/common-queries';
import { ABTestCookie, PaymentGroupName } from '@as-react/common-types';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { usePaymentState } from '../../../../context/CheckoutPaymentContext';
import { useABTest } from '../../../../hooks/useABTest';
import { useOAuth } from '../../../../hooks/useOAuth';
import { useSaferpay } from '../../../../hooks/useSaferpay';
import { analyticsActions } from '../../../../redux/analytics/actions/AnalyticsActions.creators';
import { ScrollUtil } from '../../../../util/ScrollUtil';
import { AMEX_BRAND } from '../../payment-request/PaymentRequestGroupCreditCard';
import { NewCreditCardForm } from './credit-card/NewCreditCardForm';
import { SavedCreditCardForm } from './credit-card/SavedCreditCardForm';
import { PaymentGroupContent } from './PaymentGroupContent';
export const PaymentGroupCreditCard = ({ disabled, selected, group }) => {
    const paymentGroupContentRef = useRef();
    const { t } = useTranslation(I18N_NAMESPACES.buy);
    const dispatch = useDispatch();
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const { hasBCookie: disableAmexPaymentCookie } = useABTest(ABTestCookie.DISABLE_AMEX_PAYMENT);
    const [{ optionError }] = usePaymentState();
    const optionSelectError = optionError[group.name];
    const paymentMethods = group.paymentOptions.map(option => option.name.toLowerCase());
    let groupOgoneAliases = group.ogoneAliases || [];
    if (disableAmexPaymentCookie) {
        groupOgoneAliases = groupOgoneAliases.filter(card => card.brand !== AMEX_BRAND);
    }
    const { isLoggedIn } = useOAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showError, setShowError] = useState(false);
    const [saveCreditCardForFutureUse, setSaveCreditCardForFutureUse] = useState(false);
    const [selectedSavedCard, setSelectedSavedCard] = useState(groupOgoneAliases[0]);
    const [isAddNewCardSelected, setIsAddNewCardSelected] = useState(!groupOgoneAliases.length);
    const hasSavedCards = groupOgoneAliases.length > 0;
    const { hasError: hasSaferpayError, validation } = useSaferpay({
        paymentMethods,
        cvvPlaceholder: true,
        enabled: selected && isAddNewCardSelected,
    });
    const { postSaferpayAlias, postSaferpayToken } = checkoutMutations({ servicesEndpoint, defaultRequestParams });
    const { mutate: mutatePostSaferpayAlias } = useMutation(postSaferpayAlias);
    const { mutate: mutatePostSaferpayToken } = useMutation(postSaferpayToken);
    const handleSaveCreditCardChange = () => setSaveCreditCardForFutureUse(prev => !prev);
    const handleSelectedCardChange = (card, isAddNewCardSelected) => {
        if (card)
            setSelectedSavedCard(card);
        setIsAddNewCardSelected(isAddNewCardSelected);
    };
    const handleEditSelectedCardEnabled = () => {
        setShowError(false);
    };
    const submitWithSavedCard = () => __awaiter(void 0, void 0, void 0, function* () {
        mutatePostSaferpayAlias(selectedSavedCard.id, {
            onSuccess: ({ redirectUrl }) => {
                location.href = redirectUrl;
            },
            onError: () => {
                setIsSubmitting(false);
                setShowError(true);
            },
        });
    });
    const submitWithNewCard = () => {
        window.SaferpayFields.submit({
            onSuccess: (_a) => __awaiter(void 0, [_a], void 0, function* ({ token }) {
                mutatePostSaferpayToken({ token, saveForFutureUse: saveCreditCardForFutureUse }, {
                    onSuccess: ({ redirectUrl }) => {
                        location.href = redirectUrl;
                    },
                    onError: () => {
                        setIsSubmitting(false);
                        setShowError(true);
                    },
                });
            }),
            onError: () => {
                ScrollUtil.scrollToElement(paymentGroupContentRef.current, true);
                setIsSubmitting(false);
            },
        });
    };
    const onHandleSubmit = () => {
        setIsSubmitting(true);
        setShowError(false);
        dispatch(analyticsActions.paymentRequest({ paymentGroup: PaymentGroupName.CREDITCARD }));
        if (isAddNewCardSelected) {
            submitWithNewCard();
        }
        else {
            submitWithSavedCard();
        }
    };
    const isSubmitDisabled = Boolean(disabled || !!hasSaferpayError || showError);
    const newCreditCardFormProps = {
        paymentMethods,
        validation,
        isSubmitDisabled,
        isSubmitting,
        showSaveCardBlock: Boolean(isLoggedIn && group.supportsTokenization),
        saveCreditCard: saveCreditCardForFutureUse,
        onSaveCreditCardChange: handleSaveCreditCardChange,
    };
    return (_jsx(PaymentGroupContent, { onSubmit: onHandleSubmit, selected: selected, group: group, disabled: isSubmitDisabled, isSubmitting: isSubmitting, error: optionSelectError || hasSaferpayError || showError ? t('buy.payment.form.error') : undefined, innerRef: paymentGroupContentRef, children: selected &&
            (hasSavedCards ? (_jsx(SavedCreditCardForm, { cards: groupOgoneAliases, selectedCard: selectedSavedCard, onSelectedCardChange: handleSelectedCardChange, onSubmit: onHandleSubmit, isSubmitting: isSubmitting, isSubmitDisabled: isSubmitDisabled, onEditClick: handleEditSelectedCardEnabled, hasSubmitError: Boolean(hasSaferpayError || showError), newCreditCardFormProps: Object.assign(Object.assign({}, newCreditCardFormProps), { onSubmit: onHandleSubmit }) })) : (_jsx(NewCreditCardForm, Object.assign({}, newCreditCardFormProps)))) }));
};
